// Homepage content: intro, selected-work order and the caption for each
// showcase.
//
// Every claim comes from the matching case study. A reported number keeps its
// qualification in the same `outcome.text` sentence, so the two can never be
// shown apart. Canonical titles and routes are read from portfolioWork.js,
// and the homepage shows the article's own title, so each project has one
// name. Payments and banking are project context, never the
// identity line. Official title is Senior; the homepage says "Product
// designer" and never asserts Staff.
//
// Screens, backdrops and their source sizes are recorded in
// assets/images/homepage/manifest.json (scripts/import-homepage-assets.py).

import bdBlueFold from "../assets/images/homepage/backdrops/blau-plegat.webp";
import bdIvoryLight from "../assets/images/homepage/backdrops/ivori-llum.webp";
import bdBlueCurve from "../assets/images/homepage/backdrops/corb-blau.webp";
import bdCandleStone from "../assets/images/homepage/backdrops/pedra-vela.webp";
import bdMineralBreeze from "../assets/images/homepage/backdrops/brisa-mineral.webp";
import bdSunClay from "../assets/images/homepage/backdrops/argila-sol.webp";
import bdLimewash from "../assets/images/homepage/backdrops/calma-limewash.webp";
import bdPaleClay from "../assets/images/homepage/backdrops/argila-clara.webp";
import imgHalftonePanel from "../assets/images/homepage/plugins/halftone-effect-panel.webp";
import imgHalftonePhoto from "../assets/images/homepage/plugins/halftone-source-photo.webp";
import imgJsonExportPanel from "../assets/images/homepage/plugins/screen-json-export-panel.webp";
import imgCssExporterPanel from "../assets/images/homepage/plugins/css-exporter-panel.webp";
// The desktop screens' 1600-wide stage copies (scripts/make-stage-copies.py):
// the full-size files are for the articles' zoom, and decoding them here made
// scrolling stutter.
import imgDsHero from "../assets/images/homepage/ds/site-hero-1600.webp";
import imgDsFoundation from "../assets/images/homepage/ds/site-foundation-1600.webp";
import imgDsFeatures from "../assets/images/homepage/ds/site-features-1600.webp";
import imgDsFlow from "../assets/images/homepage/ds/site-flow-1600.webp";
import imgDsArchitecture from "../assets/images/homepage/ds/site-architecture-1600.webp";
import imgDsRecipe from "../assets/images/homepage/ds/site-recipe-1600.webp";
import imgDsButton from "../assets/images/homepage/ds/site-button-1600.webp";
import imgAiAnalysis from "../assets/images/homepage/ai/analysis-1600.webp";
import imgAiRecommendation from "../assets/images/homepage/ai/recommendation-1600.webp";
import imgAiMerchantReview from "../assets/images/homepage/ai/merchant-review-1600.webp";
import imgAiClosedTicket from "../assets/images/homepage/ai/closed-ticket-1600.webp";
import imgAiTeams from "../assets/images/homepage/ai/teams-1600.webp";
import imgAiConversationMap from "../assets/images/homepage/ai/conversation-map-1600.webp";
import imgInAuthConfirm from "../assets/images/homepage/installments/auth-confirm.webp";
import imgInAuthSheetOpen from "../assets/images/homepage/installments/auth-confirm-sheet-open.webp";
import imgInAuthResult from "../assets/images/homepage/installments/auth-result.webp";
import imgInCard from "../assets/images/homepage/installments/card-detail.webp";
import imgInCardSheet from "../assets/images/homepage/installments/card-detail-sheet.webp";
import imgInPostSplit from "../assets/images/homepage/installments/post-split-selector.webp";
import imgInPostSplitOpen from "../assets/images/homepage/installments/post-split-selector-sheet-open.webp";
import imgInPostConfirm from "../assets/images/homepage/installments/post-confirm.webp";
import imgInPostResult from "../assets/images/homepage/installments/post-result.webp";
import imgInAutoSplit from "../assets/images/homepage/installments/auto-split-selector.webp";
import imgInAutoConfirm from "../assets/images/homepage/installments/auto-confirm.webp";
import imgInAutoResult from "../assets/images/homepage/installments/auto-result.webp";
import imgCoAll from "../assets/images/homepage/checkout/all.webp";
import imgCoVoucher from "../assets/images/homepage/checkout/voucher.webp";
import imgCoVoucherDetail from "../assets/images/homepage/checkout/voucher-detail.webp";
import imgCoOldBanks from "../assets/images/homepage/checkout/old-banks.webp";
import imgCoGrouped from "../assets/images/homepage/checkout/grouped.webp";
import imgCoGroupedOpen from "../assets/images/homepage/checkout/grouped-open.webp";
import imgCoSavedata from "../assets/images/homepage/checkout/savedata.webp";
import imgCoSavedataDetail from "../assets/images/homepage/checkout/savedata-detail.webp";
import imgCoThemedSavedata from "../assets/images/homepage/checkout/themed-savedata.webp";
import imgCoMerchantSite from "../assets/images/homepage/checkout/merchant-site.webp";
import imgCoThemedAll from "../assets/images/homepage/checkout/themed-all.webp";
import imgCoThemedOpen from "../assets/images/homepage/checkout/themed-open.webp";
import imgTramatBoard from "../assets/images/homepage/tramat/board.webp";
import imgTramatNotes from "../assets/images/homepage/tramat/notes.webp";
import imgMoreSneaker from "../assets/images/homepage/more/sneaker.webp";
import imgMorePortal from "../assets/images/homepage/more/portal-dashboard.webp";
import imgMoreVehicleStatus from "../assets/images/homepage/more/vehicle-status.webp";
import imgMoreWalletBalances from "../assets/images/homepage/more/wallets-balances.webp";
import imgMorePortalAiAnswer from "../assets/images/homepage/more/portal-ai-answer.webp";
import imgMoreWalletOnboarding from "../assets/images/homepage/more/wallets-onboarding.webp";
import imgMoreVehicleParking from "../assets/images/homepage/more/vehicle-parking.webp";
import imgMoreVehicle from "../assets/images/homepage/more/vehicle.webp";
import imgMoreWallets from "../assets/images/homepage/more/wallets.webp";
import imgMorePortalCanvas from "../assets/images/homepage/more/portal-canvas.webp";
import imgMoreWalletConversion from "../assets/images/homepage/more/wallets-conversion.webp";
import imgAgainTest from "../assets/images/homepage/again/test-correct.webp";
import imgAgainStreak from "../assets/images/homepage/again/streak.webp";
import { AGAIN_URL, CODE_LINKS, TRAMAT_URL, caseStudies } from "./portfolioWork";

const study = (id) => {
  const item = caseStudies.find((entry) => entry.id === id);
  if (!item) throw new Error(`homepage: no case study with id "${id}"`);
  return { title: item.title, href: item.cta.href };
};

// The title says what the work is; the subtitle continues it with how, in two
// lines at most, and the selected work below is the proof. The name is in the
// header and the city in About, so neither is repeated here.
export const intro = {
  title: "Product designer turning complex problems into products people use.",
  subtitle:
    "I take work from the first question to what ships, using systems thinking and AI to explore the options and build them.",
};

/**
 * A project visual is a stage over a calm photograph (the backdrop), with one
 * or more crops of real exports laid on it. Each layer names its source size,
 * the crop to show (source px) and where it sits on the stage (% of the stage
 * width/height, width as % of the stage width). The composition is the same
 * at every width and scales with the stage: on a phone it is a smaller copy
 * of the desktop one, and the article or the image dialog is where it is
 * read up close. `card` gives a layer an edge and shadow; `phone` puts a
 * screen in a phone's bezel and `browser` in a minimal browser window
 * ("dark" for its dark chrome); `dim` lays the stage colour over it, so it
 * reads as context behind the focal layer.
 *
 * @typedef {{crop:[number,number,number,number], at:[number,number,number]}} Placement
 * @typedef {Placement & {card?: boolean, phone?: boolean, browser?: boolean | "dark", dim?: boolean}} LayerPlacement
 * @typedef {Object} VisualLayer
 * @property {string} src                  // the screen, or the clip's first frame
 * @property {string} [video]              // a looping clip of the whole screen, /videos path without extension
 * @property {[number, number]} size       // source width, height
 * @property {boolean} [island]            // a phone screen exported without the Dynamic Island
 * @property {LayerPlacement} wide
 *
 * @typedef {Object} Backdrop
 * @property {string} src
 * @property {[number, number]} size
 * @property {string} [position]           // object-position
 * @property {number} [zoom]               // scales the crop around `position`
 *
 * @typedef {Object} Visual
 * @property {string} alt                  // describes the composition
 * @property {string} ratio                // stage aspect ratio
 * @property {Backdrop} [backdrop]
 * @property {VisualLayer[]} layers
 */

/**
 * A gallery is several real screens of one project on one stage and one
 * backdrop. The stage keeps one ratio for every slide and each composition
 * is centred on it; the controls are laid over its bottom edge, and only
 * while they are wanted. Only screens that exist in the case study belong here; with
 * fewer than two that load, the first is shown as a plain stage.
 *
 * @typedef {Object} GallerySlide
 * @property {string} label               // short name, announced on manual change
 * @property {string} alt
 * @property {VisualLayer[]} layers
 * @property {number} [hold]              // ms on screen during playback, one pass of its clip
 *
 * @typedef {Object} Gallery
 * @property {string} subject             // names the region and its controls
 * @property {string} ratio
 * @property {Backdrop} [backdrop]
 * @property {GallerySlide[]} slides
 */

/**
 * A showcase is the gallery with one caption cluster: the name, one paragraph
 * on the work (with who owned what only when leaving it out would overstate
 * it), and the outcome. A number in `outcome.value` leads the `text`, which
 * carries its meaning and its qualification. Implementation detail, rollout
 * and the full collaboration story stay in the article.
 *
 * @typedef {Object} Showcase
 * @property {string} id
 * @property {string} title             // canonical article title
 * @property {string} href
 * @property {string} summary           // context and contribution, one paragraph
 * @property {{value?:string, text:string}} outcome
 * @property {Visual} [visual]
 * @property {Gallery} [gallery]        // replaces `visual` when present
 */

// Every project stage is 15:8, the reference's 1440 × 768.
const STAGE = "15 / 8";

// One family of Mediterranean grounds, sculpted plaster and limestone in
// soft sunlight, the page opening on blue. Each stage has its own, and
// neighbouring stages differ in tone.
const backdrop = (src) => ({ src, size: [1672, 941], position: "50% 50%" });
const BLUE_FOLD = backdrop(bdBlueFold);
const IVORY_LIGHT = backdrop(bdIvoryLight);
const BLUE_CURVE = backdrop(bdBlueCurve);
const CANDLE_STONE = backdrop(bdCandleStone);
const MINERAL_BREEZE = backdrop(bdMineralBreeze);
const SUN_CLAY = backdrop(bdSunClay);
const LIMEWASH = backdrop(bdLimewash);
const PALE_CLAY = backdrop(bdPaleClay);

// Desktop screens, 3:2.
const DS_SCREEN = [2160, 1440];
const AI_SCREEN = [2880, 1920];

// A 3:2 screen in its browser window, centred: 66% of the stage wide, which
// with the title bar is about 87% of it tall, so the screen leads and the
// backdrop is a frame around it.
const WINDOW_AT = [17, 8.75, 66];

// The design system: one whole viewport of its site per slide, centred in a
// browser window, light or dark to match the capture. Each plays a loop from
// /videos over its first frame and holds for one pass of it.
const dsSlide = (src, video, browser = true) => [
  {
    src,
    video: `/videos/${video}`,
    size: DS_SCREEN,
    wide: { crop: [0, 0, 2160, 1440], at: WINDOW_AT, browser },
  },
];

// Phone screens, 750 wide, three to a slide in the order of the flow: side
// by side, about 83% of the stage tall, a quarter of a phone apart.
const PHONE_W = 20.5;
const PHONE_X = [13.85, 39.75, 65.65];
const PHONE_TOP = 8.4;
const phone = (src, height, slot) => ({
  src,
  size: [750, height],
  island: true,
  wide: { crop: [0, 0, 750, height], at: [PHONE_X[slot], PHONE_TOP, PHONE_W], phone: true },
});

// AI Workspace: the whole screen, alone and centred in its browser frame,
// like the design system's.
const aiSlide = (src) => [
  {
    src,
    size: AI_SCREEN,
    wide: { crop: [0, 0, 2880, 1920], at: WINDOW_AT, browser: true },
  },
];

/** @type {Showcase[]} */
export const showcases = [
  {
    id: "design-system",
    ...study("cs-design-system"),
    summary:
      "One foundation that people and AI build screens from, for web, iOS and Android. I designed it alone and built its first version in two months; a staff React engineer then joined to own the production package.",
    outcome: {
      value: "200+",
      text: "screens built with it across six product projects. It is now the company’s default frontend workflow.",
    },
    // The system's own site, a viewport at a time: what it promises, the one
    // foundation, what it holds, how a screen gets built, the layers a change
    // travels through, the recipe an assistant reads and a documented
    // component. Light and dark alternate.
    gallery: {
      subject: "Design system",
      ratio: STAGE,
      backdrop: BLUE_FOLD,
      slides: [
        {
          label: "From intent to interface",
          alt: "The system’s home page opening: the headline Turn product intent into working interfaces fades in, then cursors for a product manager, a product owner, a designer, a developer and an AI agent arrive around it",
          layers: dsSlide(imgDsHero, "ds-hero"),
          hold: 6000,
        },
        {
          label: "One shared foundation",
          alt: "In dark mode, a product spec, a Figma frame, design tokens, and rules and skills flow along animated lines into one foundation, which renders the same Notifications screen as the preview switches between web, iOS and Android. Below it, what the foundation serves: Figma, React, React Native, SwiftUI and AI assistants",
          layers: dsSlide(imgDsFoundation, "ds-foundation", "dark"),
          hold: 9600,
        },
        {
          label: "What it holds",
          alt: "Five animated cards: a button’s states resolving from component to semantic to primitive tokens, live components, a screen recipe, a theme scoped to one region, and a skill stepping an assistant through a build to review",
          layers: dsSlide(imgDsFeatures, "ds-features"),
          hold: 9000,
        },
        {
          label: "From frame to reviewed screen",
          alt: "Scrolling through how a screen is built, in dark mode: design with system components, attach a screen recipe, let an assistant build it, and compare and review, each step with its own animated preview",
          layers: dsSlide(imgDsFlow, "ds-flow", "dark"),
          hold: 13300,
        },
        {
          label: "Change it once",
          alt: "The architecture in five layers — primitives, semantic tokens, components, screen recipes and surfaces. One primitive changes, and its path lights up through each layer to the web app, the iOS and Android apps and the AI assistant",
          layers: dsSlide(imgDsArchitecture, "ds-architecture"),
          hold: 7000,
        },
        {
          label: "Screen recipe",
          alt: "A docs page in dark mode: a Notifications screen switches from a screenshot to its recipe, and pointing at each numbered part highlights the matching lines of its exported JSON",
          layers: dsSlide(imgDsRecipe, "ds-recipe", "dark"),
          hold: 8400,
        },
        {
          label: "A documented component",
          alt: "The Button playground in the docs: the pointer sets the appearance, intent and size and types a new label, then resets them, and the live button follows each change",
          layers: dsSlide(imgDsButton, "ds-button"),
          hold: 12900,
        },
      ],
    },
  },
  {
    id: "ai-workflow",
    ...study("cs-ai-workflow"),
    summary:
      "For each step of a merchant-support ticket, I defined what AI runs alone, what a specialist approves and what a person leads, with Operations and a software architect. The same pattern became the base of the company’s AI product development lifecycle.",
    outcome: {
      value: "−72%",
      text: "tickets needing end-to-end manual review after six months, and CSAT up 12 points, from our support analytics.",
    },
    // One ticket from the prepared case to its close, then the same workspace
    // beyond tickets: assistants in a team thread and a conversation map.
    gallery: {
      subject: "AI workflow",
      ratio: STAGE,
      backdrop: IVORY_LIGHT,
      slides: [
        {
          label: "Prepared analysis",
          alt: "AI Workspace with a suspicious transaction flagged by the Risk Assistant: the activity behind the alert, four deposits totalling 18,500 dollars in 30 minutes, and the three decisions left to the specialist: request information, mark the user as not suspicious, or block the user",
          layers: aiSlide(imgAiAnalysis),
        },
        {
          label: "Recommendation",
          alt: "AI Workspace with the Risk Assistant’s recommendation for a flagged 5,000 dollar transaction: request more information from the merchant to verify the end user’s identity and source of funds, with two actions, Request information and Show alert details",
          layers: aiSlide(imgAiRecommendation),
        },
        {
          label: "Merchant review",
          alt: "AI Workspace after the merchant answered: the assistant’s review of the two submitted documents and the sanctions screening, recommending marking the user as not suspicious, and the two decisions left to the specialist: mark the user as not suspicious or block the user",
          layers: aiSlide(imgAiMerchantReview),
        },
        {
          label: "Closing the alert",
          alt: "AI Workspace with the resolved ticket’s timeline: the alert flagged, more information requested, the email sent to the merchant, the merchant’s response analyzed and the alert closed, with John Doe marked as not suspicious",
          layers: aiSlide(imgAiClosedTicket),
        },
        {
          label: "Assistants in a team",
          alt: "AI Workspace’s Teams view for a checkout redesign, a shared thread beside the Figma file: the Figma Assistant reports seven frames updated and asks for the implementation task, and the Jira Assistant creates UX-102 and adds it to the sprint, each with Review and Undo",
          layers: aiSlide(imgAiTeams),
        },
        {
          label: "Conversation map",
          alt: "A conversation map branching from a Jira sprint health report into follow-up questions and a draft, among them the Spillover risk candidates branch, answering which issues are most at risk with five of them, with an option to include it in a new version",
          layers: aiSlide(imgAiConversationMap),
        },
      ],
    },
  },
  {
    id: "installments",
    ...study("cs-installments"),
    summary:
      "In a bank app, people decided how to pay at checkout. I proposed offering installment plans at payment authorization, keeping pay in full as the default, and carried it through with a PM and a data analyst.",
    outcome: {
      value: "+114%",
      text: "installment revenue in one year against the previous year, beating a +100% target. Authorize-and-split carried 40% of the uplift.",
    },
    // Four flows, three screens each: choosing a plan at authorization,
    // splitting after the purchase, reviewing that split, and splitting
    // automatically.
    gallery: {
      subject: "Installments",
      ratio: STAGE,
      backdrop: BLUE_CURVE,
      slides: [
        {
          label: "Choosing a plan at authorization",
          alt: "Payment authorization for 600 euros at Zara Store, with pay in full selected and the six-month and two-month plans offered below it. Next, six months selected with its cost opened: 673.56 euros in total, 73.56 euros of interest and nothing to pay today. Then the payment authorized, to be paid in six monthly payments of 112.26 euros",
          layers: [
            phone(imgInAuthConfirm, 1624, 0),
            phone(imgInAuthSheetOpen, 1624, 1),
            phone(imgInAuthResult, 1622, 2),
          ],
        },
        {
          label: "Splitting after the purchase",
          alt: "The card screen with recent purchases, each offering Split payment. Next, the Split payments sheet over it: split payments already made, or split automatically. Then choosing a plan for the 600 euro Zara Store purchase: six, four or two months",
          layers: [
            phone(imgInCard, 1624, 0),
            phone(imgInCardSheet, 1624, 1),
            phone(imgInPostSplit, 1622, 2),
          ],
        },
        {
          label: "Reviewing the split",
          alt: "Six months selected, with the monthly payment opened: 673.56 euros in total and 73.56 euros of interest. Next, the plan to review and confirm, with the date and amount of each payment. Then the purchase split successfully, with an offer to split future purchases automatically",
          layers: [
            phone(imgInPostSplitOpen, 1622, 0),
            phone(imgInPostConfirm, 1622, 1),
            phone(imgInPostResult, 1622, 2),
          ],
        },
        {
          label: "Splitting automatically",
          alt: "Setting up automatic split payments: all future purchases over 300 euros, a six-month plan, and a review before each split. Next, the summary to review before enabling it. Then automatic split enabled, with a notification before each split",
          layers: [
            phone(imgInAutoSplit, 1624, 0),
            phone(imgInAutoConfirm, 1622, 1),
            phone(imgInAutoResult, 1622, 2),
          ],
        },
      ],
    },
  },
  {
    id: "checkout",
    ...study("cs-checkout"),
    summary:
      "In a hosted checkout, I mapped four reinforcing loops behind lost payments and turned the strongest into controlled experiments, starting with paying inline. With no PM, I also owned the product requirements.",
    outcome: {
      value: "+5–6pp",
      text: "net blended completion today, measured per merchant and payment method.",
    },
    // A merchant's own checkout first, then paying inline, grouping methods
    // and payment details.
    gallery: {
      subject: "Checkout",
      ratio: STAGE,
      backdrop: CANDLE_STONE,
      slides: [
        {
          label: "Merchant theming",
          alt: "A merchant’s store with the product being bought, a MacBook Air. Next, the checkout in that merchant’s theme, listing the payment methods. Then Pix open, with its QR code and a blue Copy code action",
          layers: [
            phone(imgCoMerchantSite, 1624, 0),
            phone(imgCoThemedAll, 1624, 1),
            phone(imgCoThemedOpen, 1624, 2),
          ],
        },
        {
          label: "Paying inline",
          alt: "The checkout’s payment-method list, most used first. Next, Pix selected inline, with its QR code generated in place and a Copy code action. Then its payment details: step-by-step instructions, with Share and Download",
          layers: [
            phone(imgCoAll, 1624, 0),
            phone(imgCoVoucher, 1624, 1),
            phone(imgCoVoucherDetail, 1624, 2),
          ],
        },
        {
          label: "Grouped methods",
          alt: "The earlier checkout, listing every bank as a payment option of its own. Next, the redesign, with banks grouped under More methods, a search and the first banks. Then Itaú selected inside the group, with its QR code generated in place",
          layers: [
            phone(imgCoOldBanks, 1624, 0),
            phone(imgCoGrouped, 1624, 1),
            phone(imgCoGroupedOpen, 1624, 2),
          ],
        },
        {
          label: "Payment details",
          alt: "Pix asking for an email and ID before generating the QR code, with an option to save them for future payments. Next, the payment details: step-by-step instructions and the saved personal info, with Share and Download. Then the same details in a merchant’s theme",
          layers: [
            phone(imgCoSavedata, 1624, 0),
            phone(imgCoSavedataDetail, 1624, 1),
            phone(imgCoThemedSavedata, 1624, 2),
          ],
        },
      ],
    },
  },
];

// The card that closes the selected work: pieces from /work in an irregular
// grid, fading out under the way on. Each is cut to the shape of its cell
// (scripts/import-homepage-assets.py) and placed by `area` (home.css), which
// also decides how many show at each width; the first ones show everywhere.
// Decoration only: the card's own text names where it goes. Only pieces the
// homepage does not show (the case studies are above it, the apps and plugins
// right after), set so no two neighbours come from the same project at any
// width (home.css swaps d and h at four across for that). Interfaces only,
// apart from the sneaker: a motion study's still is mostly text.
export const moreWork = [
  { area: "a", src: imgMoreSneaker, size: [480, 727] },
  { area: "b", src: imgMorePortal, size: [1100, 811] },
  { area: "c", src: imgMoreVehicleStatus, size: [640, 472] },
  { area: "d", src: imgMoreWalletBalances, size: [1100, 394] },
  { area: "e", src: imgMoreWalletOnboarding, size: [640, 472] },
  { area: "h", src: imgMoreVehicle, size: [1100, 394] },
  { area: "f", src: imgMoreVehicleParking, size: [640, 472] },
  { area: "g", src: imgMorePortalAiAnswer, size: [640, 472] },
  { area: "i", src: imgMoreWallets, size: [640, 472] },
  { area: "j", src: imgMorePortalCanvas, size: [1100, 394] },
  { area: "k", src: imgMoreWalletConversion, size: [640, 472] },
];

// Apps and tools I designed and built, the apps first, each shown through
// the moment that makes it what it is. Every destination is another site
// (the app's own site, the plugin's Community page), so the media is not a
// link and each of `links` opens in a new tab; `name` completes a label that
// does not say what it opens. These stages are the /work tiles too.
const plugins = [
  {
    id: "design-to-code",
    name: "Design-to-code",
    kind: "Figma plugins",
    copy: "Two plugins from my design system workflow: one exports variables, styles and token collections as CSS, the other saves a screen’s structure as JSON beside its frame, for developers and AI agents to build from.",
    links: [
      { label: "CSS exporter", name: "Figma CSS Exporter", href: CODE_LINKS.cssExporter },
      { label: "JSON exporter", name: "Screen JSON Export", href: CODE_LINKS.jsonExporter },
    ],
    visual: {
      alt: "The Screen JSON Export plugin, ready to export the Dashboard frame to its attached screen.export.json layer, beside the Figma CSS Exporter with variables, styles and five token collections selected for download",
      ratio: "11 / 7",
      backdrop: PALE_CLAY,
      layers: [
        {
          src: imgJsonExportPanel,
          size: [801, 1024],
          wide: { crop: [0, 0, 801, 1024], at: [16, 14, 36], card: true },
        },
        {
          src: imgCssExporterPanel,
          size: [632, 1024],
          wide: { crop: [0, 0, 632, 1024], at: [56, 14, 28.4], card: true },
        },
      ],
    },
  },
  {
    id: "halftone",
    name: "Halftone effect",
    kind: "Figma plugin",
    copy: "Turns an image into a pattern of dots without leaving Figma, with controls for the dots’ size, shape and color.",
    links: [{ label: "View plugin", name: "Halftone effect", href: CODE_LINKS.halftone }],
    visual: {
      alt: "The Halftone effect plugin turning a horse-race photograph, shown beside it, into purple dots, with dot size, shape and color controls",
      ratio: "11 / 7",
      backdrop: LIMEWASH,
      layers: [
        {
          src: imgHalftonePhoto,
          size: [1024, 526],
          wide: { crop: [0, 0, 1024, 526], at: [13, 31.5, 46], card: true },
        },
        {
          src: imgHalftonePanel,
          size: [759, 1024],
          wide: { crop: [0, 0, 759, 1024], at: [53, 14, 34], card: true },
        },
      ],
    },
  },
];

export const built = [
  {
    id: "again",
    name: "Again",
    kind: "Vocabulary app · iOS",
    copy: "Flashcards and recall practice for the words you are learning, with courses, everyday topics and a streak of correct answers.",
    links: [{ label: "View Again", href: AGAIN_URL }],
    visual: {
      alt: "Again on iPhone: a Chinese test card answered correctly, “to listen” for 听, next to the seven-day streak secured for today",
      ratio: "11 / 7",
      backdrop: MINERAL_BREEZE,
      layers: [
        {
          src: imgAgainTest,
          size: [748, 1626],
          wide: { crop: [0, 0, 748, 1626], at: [22.9, 8, 24.6], phone: true },
        },
        {
          src: imgAgainStreak,
          size: [748, 1626],
          wide: { crop: [0, 0, 748, 1626], at: [52.5, 8, 24.6], phone: true },
        },
      ],
    },
  },
  {
    id: "tramat",
    name: "Tramat",
    kind: "Task manager · iOS",
    copy: "A simple task board with project notes. Keep everything on your device, without creating an account.",
    links: [{ label: "View Tramat", href: TRAMAT_URL }],
    visual: {
      alt: "Tramat on iPhone: the Design system project’s board, with six tasks to do and five done, next to that project’s notes",
      ratio: "11 / 7",
      backdrop: SUN_CLAY,
      layers: [
        {
          src: imgTramatBoard,
          size: [748, 1624],
          wide: { crop: [0, 0, 748, 1624], at: [22.9, 8, 24.6], phone: true },
        },
        {
          src: imgTramatNotes,
          size: [748, 1624],
          wide: { crop: [0, 0, 748, 1624], at: [52.5, 8, 24.6], phone: true },
        },
      ],
    },
  },
  ...plugins,
];
